import apiService from './api/api.service';

class PortfolioService {
  async getStudentPortfolios(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return await apiService.get(`/profile/students/list?${queryString}`);
  }

  async getConnectedStudents(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return await apiService.get(`/profile/students/connected?${queryString}`);
  }

  async getPortfolioById(portfolioId) {
    return await apiService.get(`/profile/${portfolioId}`);
  }

  async searchPortfolios(searchTerm, filters = {}) {
    const params = {
      search: searchTerm,
      ...filters,
    };
    const queryString = new URLSearchParams(params).toString();
    return await apiService.get(`/profile/students/list?${queryString}`);
  }

  async getPersonalityTraits() {
    return await apiService.get('/portfolio/personality-traits');
  }

  async getDegrees(coursePreference) {
    const url = coursePreference
      ? `/portfolio/degrees?coursePreference=${encodeURIComponent(coursePreference)}`
      : '/portfolio/degrees';
    return await apiService.get(url);
  }

  async getSpecialisationsByDegree(degree) {
    return await apiService.get(`/portfolio/specialisations?degree=${encodeURIComponent(degree)}`);
  }

  async getStates() {
    return await apiService.get('/portfolio/states');
  }

  async getDistrictsByState({ stateId, stateName } = {}) {
    const params = new URLSearchParams();
    if (stateId) params.append('stateId', stateId);
    if (stateName) params.append('stateName', stateName);
    const qs = params.toString();
    return await apiService.get(`/portfolio/districts${qs ? `?${qs}` : ''}`);
  }

  async getUniversitiesByState(state) {
    return await apiService.get(`/portfolio/universities?state=${encodeURIComponent(state)}`);
  }

  async getIndustriesByUserStage(userStage) {
    return await apiService.get(`/portfolio/industries?userStage=${userStage}`);
  }

  async getAllIndustries() {
    return await apiService.get('/portfolio/industries/all');
  }

  async getExams({ userStage, examPurposeType, isAbroad, groupByCategory } = {}) {
    const params = new URLSearchParams();
    if (userStage) params.append('userStage', userStage);
    if (examPurposeType) params.append('examPurposeType', examPurposeType);
    if (typeof isAbroad === 'boolean') params.append('isAbroad', isAbroad);
    if (groupByCategory) params.append('groupByCategory', 'true');
    const qs = params.toString();
    return await apiService.get(`/portfolio/exams${qs ? `?${qs}` : ''}`);
  }

  async getAptitudeTopics({ category, subdomain, grouped } = {}) {
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    if (subdomain) params.append('subdomain', subdomain);
    if (grouped) params.append('grouped', 'true');
    const qs = params.toString();
    return await apiService.get(`/portfolio/aptitude-topics${qs ? `?${qs}` : ''}`);
  }

  async getCareerDomains(userStage) {
    const url = userStage 
      ? `/portfolio/career-domains?userStage=${userStage}`
      : '/portfolio/career-domains';
    return await apiService.get(url);
  }

  async getJobIndustries() {
    return await apiService.get('/portfolio/job-industries');
  }

  async getJobs({ sector, industry, category } = {}) {
    const params = new URLSearchParams();
    if (sector) params.append('sector', sector);
    if (industry) params.append('industry', industry);
    if (category) params.append('category', category);
    const qs = params.toString();
    return await apiService.get(`/portfolio/jobs${qs ? `?${qs}` : ''}`);
  }

  async uploadDocument(formData) {
    return await apiService.post('/documents/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }

  async getHardSkills({ grouped } = {}) {
    const params = new URLSearchParams();
    if (grouped) params.append('grouped', 'true');
    const qs = params.toString();
    return await apiService.get(`/portfolio/hard-skills${qs ? `?${qs}` : ''}`);
  }

  async getJobProfiles({ industry, level, grouped } = {}) {
    const params = new URLSearchParams();
    if (industry) params.append('industry', industry);
    if (level) params.append('level', level);
    if (grouped) params.append('grouped', 'true');
    const qs = params.toString();
    return await apiService.get(`/portfolio/job-profiles${qs ? `?${qs}` : ''}`);
  }

  async getProfessionalSoftSkills(professionalTier) {
    if (!professionalTier) {
      throw new Error('Professional tier is required');
    }
    return await apiService.get(`/portfolio/professional-soft-skills?professionalTier=${encodeURIComponent(professionalTier)}`);
  }
}

export default new PortfolioService();
